import { LinkRun } from '@User/Data/DataSource/API/Entity/Identity'
import { identityAPI } from '@User/Data/DataSource/API/IdentityAPI'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'

const ERROR_MESSAGES: Record<string, string> = {
    BULK_LINK_DISABLED:
        'A vinculação em lote ainda está desligada (ms_bulk_link_enabled).',
    RUN_IN_PROGRESS: 'Já existe uma execução em andamento. Aguarde e tente de novo.',
    GRAPH_NOT_CONFIGURED: 'A integração com o diretório Microsoft não está configurada na API.',
    GRAPH_UNAVAILABLE: 'Não foi possível acessar o diretório Microsoft.',
}

export const identityErrorMessage = (error: unknown, fallback: string) => {
    const code = (error as { response?: { data?: { code?: string } } })?.response
        ?.data?.code
    return (code && ERROR_MESSAGES[code]) || fallback
}

/** Bulk pre-linking and directory sync runs (contracts/api-identity.md §2, §6). */
export const useIdentityLinking = () => {
    const [runs, setRuns] = useState<LinkRun[]>([])
    const [current, setCurrent] = useState<LinkRun | null>(null)
    const [running, setRunning] = useState(false)

    const loadRuns = useCallback(async () => {
        try {
            setRuns(await identityAPI.getLinkRuns())
        } catch (error) {
            console.error(error)
        }
    }, [])

    const openRun = useCallback(async (runId: string) => {
        try {
            setCurrent(await identityAPI.getLinkRun(runId))
        } catch (error) {
            console.error(error)
            toast.error('Não foi possível carregar a execução')
        }
    }, [])

    const execute = useCallback(
        async (action: () => Promise<LinkRun>, failure: string) => {
            setRunning(true)
            try {
                const run = await action()
                setCurrent(run)
                if (run.status === 'failed') {
                    toast.error(run.error || failure)
                }
                await loadRuns()
            } catch (error) {
                console.error(error)
                toast.error(identityErrorMessage(error, failure))
            }
            setRunning(false)
        },
        [loadRuns]
    )

    const preview = () =>
        execute(() => identityAPI.startLinkRun('dry-run'), 'Erro ao gerar a prévia')

    const linkAll = () =>
        execute(() => identityAPI.startLinkRun('execute'), 'Erro ao vincular os usuários')

    const syncDirectory = () =>
        execute(
            () => identityAPI.runDirectorySync(),
            'Erro ao verificar desligamentos no diretório'
        )

    useEffect(() => {
        loadRuns()
    }, [loadRuns])

    return { runs, current, running, preview, linkAll, syncDirectory, openRun }
}
